/**
 * ConditionChipsRow.jsx — iter832 (Feb 2026)
 *
 * Compact chip row shown ABOVE the BDI section on /health-intelligence/breast.
 * Each chip == a structured condition from db.health_conditions OR from the
 * onboarding wizard (hi_profile.conditions).
 *
 * iter831 features (kept):
 *   • Inline delete-X (✕) with native confirm() in Bulgarian.
 *   • Origin badge "onb" for onboarding-origin diagnoses.
 *   • Explainer line under the section title.
 *
 * iter832 additions:
 *   • Inline edit-pencil (✎) that opens a small modal to edit severity +
 *     diagnosed_at + notes for that chip. Rationale: clinical weight of
 *     "self_reported vs clinical" strongly influences BHI/BDI scoring but
 *     users rarely open Profile → Health Conditions to upgrade it.
 *   • Onboarding-origin chips: saving via ✎ triggers CONSOLIDATION
 *     (POST /consolidate/{cid}) — promotes to structured collection with
 *     the new severity + date. Manual chips: idempotent upsert via POST
 *     /health-conditions with same condition_id.
 *
 * Hover behaviour (iter820) preserved — chip ↔ BDI card cross-highlighting.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Stethoscope, X, Pencil, Loader2 } from "lucide-react";
import { useAuth } from "@/App";
import { emitHealthEvent, useHealthEvent } from "@/utils/healthEvents";
import { useBdiHighlight } from "@/utils/bdiHighlight";

// iter821 — one-shot pulse hint.
const CHIP_HINT_KEY = "bhi_chip_hint_shown";

const CATEGORY_ACCENT = {
  breast_benign:      "bg-pink-100 text-pink-800 ring-pink-300",
  breast_benign_male: "bg-sky-100 text-sky-800 ring-sky-300",
  endocrine:          "bg-purple-100 text-purple-800 ring-purple-300",
  metabolic:          "bg-amber-100 text-amber-800 ring-amber-300",
  cardio:             "bg-rose-100 text-rose-800 ring-rose-300",
  autoimmune:         "bg-orange-100 text-orange-800 ring-orange-300",
  inflammation:       "bg-red-100 text-red-800 ring-red-300",
  nutrition:          "bg-emerald-100 text-emerald-800 ring-emerald-300",
  genetic:            "bg-indigo-100 text-indigo-800 ring-indigo-300",
  other:              "bg-slate-100 text-slate-800 ring-slate-300",
};

const SEVERITY_LABELS_BG = {
  mild:          "Лека",
  moderate:      "Умерена",
  severe:        "Тежка",
  clinical:      "Клинично потвърдена",
  self_reported: "Самодекларирана",
};

// Honest UX copy — no fabricated multipliers. Explains WHY severity
// matters clinically without inventing specific numbers (Truthful Patterns).
const SEVERITY_WEIGHT_BG = {
  mild:          "Лека форма — базово влияние в BHI/BDI изчисленията.",
  moderate:      "Умерена — стандартно клинично тегло.",
  severe:        "Тежка — засилва приоритета в plans и препоръки.",
  clinical:      "Клинично потвърдена от лекар — максимална достоверност.",
  self_reported: "Самодекларирана — с намалено тегло, докато не се потвърди.",
};

export default function ConditionChipsRow({ isDark = false }) {
  const { API, user } = useAuth();
  const [items, setItems] = useState([]);
  const [pulseHint, setPulseHint] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editing, setEditing] = useState(null);       // full item being edited
  const [editForm, setEditForm] = useState(null);      // { severity, diagnosed_at, notes }
  const [saving, setSaving] = useState(false);
  const {
    hoveredCondition, hoveredDiseaseId,
    cardsByCondition, conditionsByCard,
    setHoveredCondition,
  } = useBdiHighlight();

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/health-conditions`);
      setItems(data?.items || []);
    } catch { setItems([]); }
  }, [API, user]);

  useEffect(() => { load(); }, [load]);
  useHealthEvent("conditions-changed", () => { load(); });

  useEffect(() => {
    if (items.length === 0) return;
    try {
      if (localStorage.getItem(CHIP_HINT_KEY)) return;
      setPulseHint(true);
      const t = setTimeout(() => {
        setPulseHint(false);
        try { localStorage.setItem(CHIP_HINT_KEY, "1"); } catch { /* private mode */ }
      }, 6000);
      return () => clearTimeout(t);
    } catch { /* localStorage unavailable */ }
  }, [items.length]);

  const dismissPulse = useCallback(() => {
    if (!pulseHint) return;
    setPulseHint(false);
    try { localStorage.setItem(CHIP_HINT_KEY, "1"); } catch { /* noop */ }
  }, [pulseHint]);

  // iter831 — inline delete
  const handleDelete = useCallback(async (item) => {
    const name = item.condition || item.condition_id;
    const originMsg = item.origin === "onboarding"
      ? "\n\n(Тази диагноза беше добавена от onboarding въпросника.)"
      : "";
    const ok = window.confirm(
      `Сигурни ли сте, че искате да премахнете «${name}»?${originMsg}\n\n` +
      "Това ще изчисти влиянието ѝ върху BDI картите, Twin индекса и пътищата."
    );
    if (!ok) return;
    setDeletingId(item.id);
    try {
      await axios.delete(`${API}/health-conditions/${encodeURIComponent(item.id)}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      emitHealthEvent("conditions-changed", {
        removed: [item.condition_id].filter(Boolean),
        source: "bhi_chip_row",
      });
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Грешка при премахване на диагнозата.");
    } finally {
      setDeletingId(null);
    }
  }, [API]);

  // iter832 — open edit modal
  const openEdit = useCallback((item) => {
    setEditing(item);
    setEditForm({
      severity:     item.severity || "self_reported",
      diagnosed_at: item.diagnosed_at || "",
      notes:        item.notes || "",
    });
  }, []);

  const closeEdit = useCallback(() => {
    if (saving) return;
    setEditing(null);
    setEditForm(null);
  }, [saving]);

  // iter832 — save edit; consolidate onboarding chips into structured collection.
  const saveEdit = useCallback(async () => {
    if (!editing || !editForm) return;
    setSaving(true);
    try {
      if (editing.origin === "onboarding") {
        // Promote to structured with new details; backend removes onboarding entry.
        await axios.post(
          `${API}/health-conditions/consolidate/${editing.condition_id}`,
          {
            severity:     editForm.severity,
            diagnosed_at: editForm.diagnosed_at,
            notes:        editForm.notes,
          },
        );
      } else {
        // Idempotent upsert on manual chip.
        await axios.post(`${API}/health-conditions`, {
          condition_id: editing.condition_id,
          severity:     editForm.severity,
          diagnosed_at: editForm.diagnosed_at,
          notes:        editForm.notes,
        });
      }
      emitHealthEvent("conditions-changed", {
        updated: [editing.condition_id],
        source: "bhi_chip_row_edit",
      });
      await load();
      setEditing(null);
      setEditForm(null);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Грешка при запазване.");
    } finally {
      setSaving(false);
    }
  }, [API, editing, editForm, load]);

  const litFromCardMemo = useMemo(
    () => (hoveredDiseaseId ? (conditionsByCard[hoveredDiseaseId] || new Set()) : new Set()),
    [hoveredDiseaseId, conditionsByCard],
  );

  if (!user || items.length === 0) return null;

  const litFromCard = litFromCardMemo;

  return (
    <>
      <div
        data-testid="condition-chips-row"
        className={`mb-4 p-3 rounded-xl border ${isDark ? "bg-slate-900/60 border-slate-700" : "bg-white border-slate-200"}`}
      >
        <div className={`text-[10px] uppercase font-bold tracking-widest mb-1 flex items-center gap-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          <Stethoscope className="w-3 h-3" />
          Вашите диагнози · задръжте върху някоя, за да видите кои карти по-долу засяга
          {pulseHint && (
            <span
              className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-400/20 text-amber-700 dark:text-amber-300 animate-pulse"
              data-testid="chip-hint-badge"
            >
              интерактивно →
            </span>
          )}
        </div>
        <div className={`text-[11px] mb-2 leading-snug ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          Идват от onboarding въпросника или от <span className="font-semibold">Профил → Здравословни състояния</span>.
          <span className="ml-1">Редактирайте с</span>
          <span className="inline-flex items-center justify-center align-middle mx-1 w-3.5 h-3.5 rounded-full bg-indigo-500/15 text-indigo-600 dark:text-indigo-300">
            <Pencil className="w-2 h-2" strokeWidth={3} />
          </span>
          или премахнете с
          <span className="inline-flex items-center justify-center align-middle mx-1 w-3.5 h-3.5 rounded-full bg-rose-500/15 text-rose-600 dark:text-rose-300">
            <X className="w-2.5 h-2.5" strokeWidth={3} />
          </span>
          върху съответния чип.
        </div>
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, idx) => {
            const cid = it.condition_id;
            const affectedCards = cardsByCondition[cid] || new Set();
            const affectsCount = affectedCards.size;
            const isHoveredHere = hoveredCondition === cid;
            const isLitFromCard = litFromCard.has(cid);
            const isOnboarding = it.origin === "onboarding";
            const accent = CATEGORY_ACCENT[it.category] || CATEGORY_ACCENT.other;
            const ring =
              isHoveredHere || isLitFromCard
                ? "ring-2 ring-amber-500 shadow-[0_0_0_4px_rgba(245,158,11,0.20)] scale-[1.03]"
                : "ring-1";
            const pulseCls =
              pulseHint && idx === 0
                ? "animate-pulse ring-2 ring-amber-400 shadow-[0_0_0_6px_rgba(245,158,11,0.28)]"
                : "";
            const isDeleting = deletingId === it.id;
            return (
              <div
                key={it.id || cid}
                onMouseEnter={() => { setHoveredCondition(cid); dismissPulse(); }}
                onMouseLeave={() => setHoveredCondition(null)}
                onFocus={() => { setHoveredCondition(cid); dismissPulse(); }}
                onBlur={() => setHoveredCondition(null)}
                data-testid={`condition-chip-${cid}`}
                title={affectsCount > 0 ? `Влияе на ${affectsCount} карт${affectsCount === 1 ? "а" : "и"} по-долу` : "Няма съвпадащи пътища с текущите карти"}
                tabIndex={0}
                className={`inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-[11px] font-semibold transition-all cursor-default ${accent} ${ring} ${pulseCls} ${isDeleting ? "opacity-50" : ""}`}
              >
                <span>{it.condition || cid}</span>
                {isOnboarding && (
                  <span
                    className="inline-flex items-center px-1 rounded-full bg-white/60 dark:bg-slate-900/40 text-[9px] font-bold uppercase tracking-wide opacity-80"
                    title="Добавена от onboarding въпросника (self_reported). Натиснете ✎, за да я потвърдите клинично."
                    data-testid={`condition-origin-onb-${cid}`}
                  >
                    onb
                  </span>
                )}
                {affectsCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                    {affectsCount}
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openEdit(it); }}
                  disabled={isDeleting}
                  data-testid={`condition-chip-edit-${cid}`}
                  aria-label={`Редактирай ${it.condition || cid}`}
                  title="Редактирай тежест / дата / бележка"
                  className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/60 hover:bg-indigo-500 hover:text-white text-slate-500 dark:bg-slate-800/60 dark:text-slate-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Pencil className="w-2.5 h-2.5" strokeWidth={3} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(it); }}
                  disabled={isDeleting}
                  data-testid={`condition-chip-delete-${cid}`}
                  aria-label={`Премахни ${it.condition || cid}`}
                  title="Премахни тази диагноза"
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/60 hover:bg-rose-500 hover:text-white text-slate-500 dark:bg-slate-800/60 dark:text-slate-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <X className="w-3 h-3" strokeWidth={3} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* iter832 — Inline edit modal */}
      {editing && editForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={closeEdit}
          data-testid="condition-edit-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl border shadow-2xl ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"}`}
            data-testid="condition-edit-modal"
          >
            <div className={`px-5 py-4 border-b ${isDark ? "border-slate-800" : "border-slate-100"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-[10px] uppercase font-bold tracking-widest text-indigo-500">
                    Редактиране на диагноза
                  </div>
                  <h3 className={`text-lg font-extrabold mt-0.5 ${isDark ? "text-white" : "text-slate-900"}`}>
                    {editing.condition || editing.condition_id}
                  </h3>
                  {editing.origin === "onboarding" && (
                    <p className={`text-[11px] mt-1 leading-snug ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                      Диагнозата идва от onboarding. Запазването ще я премести в структурираните ви записи с новите детайли.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={saving}
                  className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500"}`}
                  data-testid="condition-edit-close"
                  aria-label="Затвори"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className={`text-[11px] uppercase font-bold tracking-wide block mb-1.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  Тежест / клиничен статус
                </label>
                <select
                  value={editForm.severity}
                  onChange={(e) => setEditForm({ ...editForm, severity: e.target.value })}
                  disabled={saving}
                  data-testid="condition-edit-severity"
                  className={`w-full px-3 py-2 rounded-lg border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                    isDark ? "bg-slate-800 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"
                  }`}
                >
                  {Object.entries(SEVERITY_LABELS_BG).map(([val, lbl]) => (
                    <option key={val} value={val}>{lbl}</option>
                  ))}
                </select>
                <p className={`text-[11px] mt-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {SEVERITY_WEIGHT_BG[editForm.severity] || ""}
                </p>
              </div>

              <div>
                <label className={`text-[11px] uppercase font-bold tracking-wide block mb-1.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  Дата на диагностициране
                </label>
                <input
                  type="date"
                  value={editForm.diagnosed_at || ""}
                  onChange={(e) => setEditForm({ ...editForm, diagnosed_at: e.target.value })}
                  disabled={saving}
                  data-testid="condition-edit-date"
                  className={`w-full px-3 py-2 rounded-lg border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                    isDark ? "bg-slate-800 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"
                  }`}
                />
                <p className={`text-[11px] mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  По желание — помага за времеви анализи.
                </p>
              </div>

              <div>
                <label className={`text-[11px] uppercase font-bold tracking-wide block mb-1.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  Бележки
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value.slice(0, 500) })}
                  disabled={saving}
                  rows={3}
                  maxLength={500}
                  placeholder="Напр. лекуващ лекар, доза лекарство, ключови стойности..."
                  data-testid="condition-edit-notes"
                  className={`w-full px-3 py-2 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                    isDark ? "bg-slate-800 border-slate-700 text-white placeholder-slate-500" : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
                  }`}
                />
                <p className={`text-[10px] mt-1 text-right ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                  {editForm.notes.length}/500
                </p>
              </div>
            </div>

            <div className={`px-5 py-3 border-t flex items-center justify-end gap-2 ${isDark ? "border-slate-800 bg-slate-950/40" : "border-slate-100 bg-slate-50/60"}`}>
              <button
                type="button"
                onClick={closeEdit}
                disabled={saving}
                data-testid="condition-edit-cancel"
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isDark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                Отказ
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={saving}
                data-testid="condition-edit-save"
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {saving ? "Запазване..." : "Запази"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
